import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toBlob } from 'html-to-image';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { CreativeCanvas, SIZES, TEMPLATES, DEFAULT_OVERRIDES } from '../components/CreativeCanvas';
import { BrandedCampaignBuilder } from '../components/BrandedCampaignBuilder';
import { AIPosterStudio } from '../components/AIPosterStudio';
import { CarouselStudio } from '../components/CarouselStudio';
import { useFeature, FeatureLockedCard } from '../hooks/useFeatures';
import {
  Sparkles, Loader2, Wand2, Save, Download, Image as ImageIcon, Upload, Palette, Move, RotateCcw, Megaphone, AlertTriangle, Images,
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORMS = ['Instagram', 'Facebook', 'LinkedIn', 'TikTok', 'Bluesky', 'X'];
const TONES = ['Professional', 'Friendly', 'Bold', 'Playful', 'Inspirational', 'Luxury'];

export const CreativeStudioPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  const [kits, setKits] = useState([]);
  const [kitId, setKitId] = useState('');
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('Instagram');
  const [tone, setTone] = useState('Professional');
  const [size, setSize] = useState('1:1');
  const [template, setTemplate] = useState('spotlight');
  const [bgImage, setBgImage] = useState(null);
  const [content, setContent] = useState({ headline: 'Your headline here', subheadline: 'A short supporting line for your creative', cta: 'Learn more', caption: '', hashtags: '' });
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [generatingBg, setGeneratingBg] = useState(false);
  const [ov, setOv] = useState(DEFAULT_OVERRIDES);
  const [exporting, setExporting] = useState(false);
  const [mode, setMode] = useState('single');
  const dragRef = useRef(null);
  const csFeature = useFeature('creative_studio');

  const { w, h } = SIZES[size];
  const previewScale = 360 / w;

  const setOvK = (k, v) => setOv((o) => ({ ...o, [k]: v }));

  // Pointer-drag the text block on the preview. Screen deltas are divided by the
  // preview scale so the move maps 1:1 onto the full-resolution export canvas.
  const onDragStart = (e) => {
    e.preventDefault();
    dragRef.current = { x: e.clientX, y: e.clientY, tx: ov.tx, ty: ov.ty };
    const move = (me) => {
      const d = dragRef.current;
      if (!d) return;
      setOv((o) => ({
        ...o,
        tx: Math.round(d.tx + (me.clientX - d.x) / previewScale),
        ty: Math.round(d.ty + (me.clientY - d.y) / previewScale),
      }));
    };
    const up = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  useEffect(() => {
    api.get('/brand-kits').then((r) => {
      const list = r.data?.brand_kits || [];
      setKits(list);
      if (list[0]) setKitId(list[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const kit = kits.find((k) => k.id === kitId) || null;
  const set = (k, v) => setContent((c) => ({ ...c, [k]: v }));

  const generate = async () => {
    if (topic.trim().length < 3) { toast.error('Enter a topic first'); return; }
    setGenerating(true);
    try {
      const r = await api.post('/creatives/generate-copy', { topic, platform, tone, brand_name: kit?.name || null });
      setContent({ headline: r.data.headline, subheadline: r.data.subheadline, cta: r.data.cta, caption: r.data.caption, hashtags: r.data.hashtags });
      toast.success('Creative copy generated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const uploadBg = async (file) => {
    if (!file) return;
    setUploadingBg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/uploads/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBgImage(r.data.url);
      toast.success('Background added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingBg(false); }
  };

  // Generate an AI background photo (Nano Banana) behind the overlay. Returns a
  // base64 data URL so html-to-image can export it without CORS tainting.
  const generateBg = async () => {
    const prompt = (topic.trim() || content.headline || '').trim();
    if (prompt.length < 3) { toast.error('Enter a topic to generate a background'); return; }
    setGeneratingBg(true);
    try {
      const r = await api.post('/generate/image', { prompt, model: 'nano-banana' });
      if (r.data?.image_base64) {
        setBgImage(`data:image/png;base64,${r.data.image_base64}`);
        toast.success('AI background generated');
      } else {
        toast.error('No image returned');
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Background generation failed'));
    } finally {
      setGeneratingBg(false);
    }
  };

  const renderBlob = async () => {
    const node = canvasRef.current;
    const { w: rw, h: rh } = SIZES[size];
    // Hide the dashed edit affordance during capture.
    setExporting(true);
    await new Promise((r) => setTimeout(r, 60));
    try {
      // html-to-image occasionally produces a blank first frame in webkit — render twice.
      await toBlob(node, { width: rw, height: rh, cacheBust: true, pixelRatio: 1, skipFonts: true });
      return await toBlob(node, { width: rw, height: rh, cacheBust: true, pixelRatio: 1, skipFonts: true });
    } finally {
      setExporting(false);
    }
  };

  const download = async () => {
    try {
      const blob = await renderBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `creative-${Date.now()}.png`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Image downloaded');
    } catch (e) { toast.error('Could not render image'); }
  };

  const saveAsDraft = async () => {
    if (!content.caption?.trim()) { toast.error('Generate copy first (need a caption)'); return; }
    setSaving(true);
    try {
      const blob = await renderBlob();
      const file = new File([blob], `creative-${Date.now()}.png`, { type: 'image/png' });
      const fd = new FormData();
      fd.append('file', file);
      const up = await api.post('/uploads/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post('/posts', {
        caption: content.caption,
        hashtags: content.hashtags,
        platforms: [platform],
        post_title: content.headline,
        media_items: [up.data],
      });
      toast.success('Saved as draft in My Posts');
      navigate('/posts');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save creative'); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="creative-studio-page">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2"><Sparkles className="w-6 h-6 text-indigo-600" /> AI Creative Studio</h1>
          <p className="text-sm text-zinc-500 mt-1">Design one branded creative, or auto-create a whole branded campaign with overlaid images.</p>
        </div>

        {csFeature.locked ? (
          <FeatureLockedCard
            featureName={csFeature.featureName || 'AI Creative Studio'}
            requiredTiers={csFeature.requiredTiers}
            description={csFeature.description}
            testId="creative-studio-locked"
          />
        ) : (
        <>
        {/* Mode toggle */}
        <div className="inline-flex rounded-lg border border-zinc-200 bg-zinc-50 p-1" data-testid="studio-mode-toggle">
          <button onClick={() => setMode('single')} data-testid="studio-mode-single"
            className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${mode === 'single' ? 'bg-white shadow-sm text-indigo-700' : 'text-zinc-500 hover:text-zinc-700'}`}>
            Single creative
          </button>
          <button onClick={() => setMode('campaign')} data-testid="studio-mode-campaign"
            className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${mode === 'campaign' ? 'bg-white shadow-sm text-indigo-700' : 'text-zinc-500 hover:text-zinc-700'}`}>
            <Megaphone className="w-3.5 h-3.5" /> Branded campaign
          </button>
          <button onClick={() => setMode('poster')} data-testid="studio-mode-poster"
            className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${mode === 'poster' ? 'bg-white shadow-sm text-indigo-700' : 'text-zinc-500 hover:text-zinc-700'}`}>
            <Sparkles className="w-3.5 h-3.5" /> AI Poster
          </button>
          <button onClick={() => setMode('carousel')} data-testid="studio-mode-carousel"
            className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 ${mode === 'carousel' ? 'bg-white shadow-sm text-indigo-700' : 'text-zinc-500 hover:text-zinc-700'}`}>
            <Images className="w-3.5 h-3.5" /> AI Carousel
          </button>
        </div>

        {mode === 'poster' && <AIPosterStudio kits={kits} />}

        {mode === 'carousel' && <CarouselStudio kits={kits} />}

        {mode === 'campaign' && <BrandedCampaignBuilder kits={kits} />}

        {mode === 'single' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Controls */}
          <div className="space-y-4">
            <Card><CardContent className="p-4 space-y-4">
              <div className="space-y-1">
                <Label className="text-xs">What's the creative about?</Label>
                <Textarea rows={3} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Announce our weekend 20% off sale on all coffee blends" data-testid="creative-topic" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Platform</Label>
                  <Select value={platform} onValueChange={setPlatform}><SelectTrigger data-testid="creative-platform"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tone</Label>
                  <Select value={tone} onValueChange={setTone}><SelectTrigger data-testid="creative-tone"><SelectValue /></SelectTrigger>
                    <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Size</Label>
                  <Select value={size} onValueChange={setSize}><SelectTrigger data-testid="creative-size"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(SIZES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Brand kit</Label>
                  <Select value={kitId || 'none'} onValueChange={(v) => setKitId(v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="creative-brandkit"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Default colors</SelectItem>
                      {kits.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
                    </SelectContent></Select>
                </div>
              </div>
              {platform === 'Instagram' && size === '9:16' && (
                <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="creative-ig-ratio-warning">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>9:16 is for Stories/Reels — an Instagram <strong>feed</strong> post will crop this and may clip your overlay text. Use <strong>1:1</strong> or <strong>4:5</strong> for feed.</span>
                </div>
              )}
              {kits.length === 0 && (
                <button onClick={() => navigate('/brand-kit')} className="text-xs text-indigo-600 hover:underline flex items-center gap-1" data-testid="creative-create-kit-link">
                  <Palette className="w-3.5 h-3.5" /> Create a brand kit to auto-apply your colors & logo
                </button>
              )}
              <Button onClick={generate} disabled={generating} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="generate-copy-btn">
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />} Generate copy with AI
              </Button>
            </CardContent></Card>

            {/* Template + content editing */}
            <Card><CardContent className="p-4 space-y-4">
              <div>
                <Label className="text-xs">Template</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {TEMPLATES.map((t) => (
                    <button key={t.id} onClick={() => setTemplate(t.id)} data-testid={`template-${t.id}`}
                      className={`text-left rounded-md border p-2 transition-colors ${template === t.id ? 'border-indigo-500 bg-indigo-50' : 'border-zinc-200 hover:border-zinc-300'}`}>
                      <p className="text-xs font-semibold">{t.name}</p>
                      <p className="text-[10px] text-zinc-500 leading-tight">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div><Label className="text-xs">Headline</Label><Input value={content.headline} onChange={(e) => set('headline', e.target.value)} data-testid="edit-headline" /></div>
                <div><Label className="text-xs">Subheadline</Label><Input value={content.subheadline} onChange={(e) => set('subheadline', e.target.value)} data-testid="edit-subheadline" /></div>
                <div><Label className="text-xs">CTA</Label><Input value={content.cta} onChange={(e) => set('cta', e.target.value)} data-testid="edit-cta" /></div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Background image (optional)</Label>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBg(e.target.files?.[0])} data-testid="creative-bg-input" />
                    <span className="inline-flex items-center text-sm border border-zinc-200 rounded-md px-3 h-9 hover:bg-zinc-50">
                      {uploadingBg ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} {bgImage ? 'Replace' : 'Upload'}
                    </span>
                  </label>
                  {bgImage && <Button variant="ghost" size="sm" onClick={() => setBgImage(null)}>Remove</Button>}
                  <span className="text-[11px] text-zinc-400">{bgImage ? 'Using image' : 'Else brand gradient'}</span>
                </div>
                <Button variant="outline" size="sm" className="w-full mt-1" onClick={generateBg} disabled={generatingBg} data-testid="creative-generate-bg">
                  {generatingBg ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5 text-indigo-600" />}
                  Generate AI background (Nano Banana)
                </Button>
                <p className="text-[10.5px] text-zinc-400">Uses your topic as the prompt. Counts toward your monthly AI image quota.</p>
              </div>

              {/* Phase 2 — lightweight layout editor */}
              <div className="pt-2 border-t border-zinc-100 space-y-3" data-testid="creative-edit-layout">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold flex items-center gap-1.5"><Move className="w-3.5 h-3.5 text-indigo-600" /> Edit layout</Label>
                  <button onClick={() => setOv(DEFAULT_OVERRIDES)} className="text-[11px] text-zinc-500 hover:text-indigo-600 flex items-center gap-1" data-testid="creative-reset-layout">
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[11px] text-zinc-500">Headline size</Label>
                    <input type="range" min="0.6" max="1.6" step="0.05" value={ov.headlineScale}
                      onChange={(e) => setOvK('headlineScale', parseFloat(e.target.value))}
                      className="w-full accent-indigo-600" data-testid="creative-headline-size" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-zinc-500">Subheadline size</Label>
                    <input type="range" min="0.6" max="1.6" step="0.05" value={ov.subScale}
                      onChange={(e) => setOvK('subScale', parseFloat(e.target.value))}
                      className="w-full accent-indigo-600" data-testid="creative-sub-size" />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-[11px] text-zinc-500">Overlay darkness</Label>
                    <input type="range" min="0" max="0.95" step="0.05" value={ov.overlay}
                      onChange={(e) => setOvK('overlay', parseFloat(e.target.value))}
                      className="w-full accent-indigo-600" data-testid="creative-overlay" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-[11px] text-zinc-600 cursor-pointer">
                    Text color
                    <input type="color" value={ov.textColor || '#ffffff'} onChange={(e) => setOvK('textColor', e.target.value)}
                      className="w-7 h-7 rounded border border-zinc-200 cursor-pointer p-0" data-testid="creative-text-color" />
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-zinc-600 cursor-pointer">
                    Accent / CTA
                    <input type="color" value={ov.accentColor || kit?.accent_color || '#f59e0b'} onChange={(e) => setOvK('accentColor', e.target.value)}
                      className="w-7 h-7 rounded border border-zinc-200 cursor-pointer p-0" data-testid="creative-accent-color" />
                  </label>
                </div>
              </div>
            </CardContent></Card>
          </div>

          {/* Preview + actions */}
          <div className="space-y-4">
            <Card><CardContent className="p-4 flex flex-col items-center gap-4">
              <div className="rounded-lg overflow-hidden shadow-lg border border-zinc-200" style={{ width: w * previewScale, height: h * previewScale }} data-testid="creative-canvas">
                <div style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left', width: w, height: h }}>
                  <CreativeCanvas ref={canvasRef} template={template} size={size} kit={kit} content={content} bgImage={bgImage} overrides={ov} editable={!exporting} exporting={exporting} onDragStart={onDragStart} />
                </div>
              </div>
              <p className="text-[11px] text-zinc-400 flex items-center gap-1 -mt-1" data-testid="creative-drag-hint">
                <Move className="w-3 h-3" /> Drag the text on the preview to reposition it
              </p>
              <div className="flex gap-2 w-full">
                <Button onClick={saveAsDraft} disabled={saving} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-creative-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save as draft post
                </Button>
                <Button variant="outline" onClick={download} data-testid="download-creative-btn"><Download className="w-4 h-4 mr-2" /> PNG</Button>
              </div>
              {content.caption && (
                <div className="w-full text-xs bg-zinc-50 border border-zinc-200 rounded-md p-3 space-y-1" data-testid="creative-caption-preview">
                  <p className="font-semibold text-zinc-500 uppercase text-[10px] flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Caption</p>
                  <p className="text-zinc-700 whitespace-pre-wrap">{content.caption}</p>
                  <p className="text-indigo-600">{content.hashtags}</p>
                </div>
              )}
            </CardContent></Card>
          </div>
        </div>
        )}
        </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CreativeStudioPage;
